import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Calendar,
    Clock,
    PhoneCall,
    MessageCircle,
    Trash2,
    Scissors,
    Loader2,
    AlertTriangle,
    LockOpen,
    UserX,
    Bell
} from 'lucide-react';
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { toast } from 'sonner';

import Modal from '../ui/Modal';
import CustomSelect from '../ui/CustomSelect';
import ConfirmDialog from '../ui/ConfirmDialog';
import ReportCustomerModal from './ReportCustomerModal';
import TechnicalNotesPanel from '../customers/TechnicalNotesPanel';
import { getWhatsAppUrl } from '@/utils/whatsapp';
import { useEvents } from '@/contexts/EventsContext';
import { useAuth } from '@/contexts/AuthContext';
import { useClients } from '@/contexts/ClientsContext';
import { useUsers } from '@/contexts/UsersContext';
import { fetchLatestBookingDiscount, recordBookingDiscount, BookingDiscount } from '@/services/events/bookingDiscountService';

interface BookingModalProps {
    isOpen: boolean;
    booking: any | null;
    onClose: () => void;
    onOpenCustomer?: (client: any) => void;
    hairdresser?: any | null;
    client?: any | null;
    performance?: any | null;
}

const BookingModal: React.FC<BookingModalProps> = ({
    isOpen,
    booking,
    onClose,
    onOpenCustomer,
    hairdresser = null,
    client = null,
    performance = null,
}) => {
    const { t, i18n } = useTranslation();
    const { deleteBooking, updateBooking, getPerformancesByHairdresser } = useEvents();
    const { organization, user } = useAuth();
    const { createCustomerReport, blackList, fetchBlackList, customerReports: allCustomerReports, blockClient, unlockClient } = useClients();

    // Stati per la cancellazione
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Stati per la segnalazione
    const customerReports = useMemo(() => {
        if (!client?.id || !allCustomerReports) return [];
        return allCustomerReports.filter((r: any) => r.client_id === client.id);
    }, [client?.id, allCustomerReports]);
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const [isReporting, setIsReporting] = useState(false);

    const maxReportsLimit = organization?.max_reports_limit ?? 3;
    const willBlockOnNextReport = customerReports.length >= maxReportsLimit - 1;
    const isBlocked = client?.id && blackList.includes(client.id);

    const [confirmAction, setConfirmAction] = useState<'block' | 'unblock' | null>(null);
    const [isActionLoading, setIsActionLoading] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [localStatus, setLocalStatus] = useState<string>(booking?.status || 'confirmed');
    const { users } = useUsers();

    // Sconto applicato (audit): presente quando price_adjustment è negativo.
    // In stato locale perché il prop booking (copia in Calendar) non si aggiorna dopo updateBooking.
    const [localAdjustment, setLocalAdjustment] = useState<number>(Number(booking?.price_adjustment) || 0);
    const hasDiscount = localAdjustment < 0;
    const discountEuro = Math.abs(localAdjustment);
    const [discount, setDiscount] = useState<BookingDiscount | null>(null);
    const [isDiscounting, setIsDiscounting] = useState(false);

    useEffect(() => {
        if (booking?.status) {
            setLocalStatus(booking.status);
        }
        setLocalAdjustment(Number(booking?.price_adjustment) || 0);
    }, [booking]);

    useEffect(() => {
        let active = true;
        if (isOpen && hasDiscount && booking?.id) {
            fetchLatestBookingDiscount(booking.id).then((d) => { if (active) setDiscount(d); });
        } else {
            setDiscount(null);
        }
        return () => { active = false; };
    }, [isOpen, booking?.id, hasDiscount]);

    const discountAppliedBy = useMemo(() => {
        if (!discount?.applied_by || !users) return null;
        const u = users.find((x: any) => x.id === discount.applied_by);
        return u ? `${u.first_name} ${u.last_name}` : null;
    }, [discount, users]);

    const bookingStart = useMemo(() => {
        if (!booking?.date || !booking?.time) return null;
        return new Date(`${booking.date}T${booking.time.slice(0, 5)}:00`);
    }, [booking?.date, booking?.time]);

    const isPastOrPresent = useMemo(() => {
        if (!bookingStart) return false;
        const now = new Date();
        return now >= bookingStart;
    }, [bookingStart]);

    // Il no-show si registra con un solo click: nessuna conferma intermedia.
    // Chi vuole anche segnalare il cliente usa il pulsante "Segnala" accanto.
    const handleNoShow = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!booking?.id) return;
        setIsUpdatingStatus(true);
        try {
            const result = await updateBooking(booking.id, { status: 'no_show' });
            if (result.success) {
                setLocalStatus('no_show');
                toast.success(t('bookings.no_show.success', 'Segnato come non presentato'));
            } else {
                toast.error(result.error || t('common.error'));
            }
        } catch (error) {
            console.error('Error setting no-show status:', error);
            toast.error(t('common.error'));
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    // Ripristina un appuntamento erroneamente segnato come no-show: torna a "confermato".
    const handleUndoNoShow = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!booking?.id) return;
        setIsUpdatingStatus(true);
        try {
            const result = await updateBooking(booking.id, { status: 'confirmed' });
            if (result.success) {
                setLocalStatus('confirmed');
                toast.success(t('bookings.modal.no_show_undo_success', 'Stato ripristinato: appuntamento confermato'));
            } else {
                toast.error(result.error || t('common.error'));
            }
        } catch (error) {
            console.error('Error undoing no-show status:', error);
            toast.error(t('common.error'));
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const servicePrice = Number(performance?.price) || 0;

    // Sconto o maggiorazione a valore libero, come nel mockup: il campo scrive
    // direttamente price_adjustment (negativo = sconto) e registra l'audit.
    const [adjustmentType, setAdjustmentType] = useState<'discount' | 'markup'>('discount');
    const [adjustmentInput, setAdjustmentInput] = useState('');
    useEffect(() => {
        const adj = Number(booking?.price_adjustment) || 0;
        setAdjustmentType(adj > 0 ? 'markup' : 'discount');
        setAdjustmentInput(adj === 0 ? '' : String(Math.abs(adj)));
    }, [booking?.id, booking?.price_adjustment]);

    const adjustmentExceedsPrice =
        adjustmentType === 'discount' && servicePrice > 0 && Number(adjustmentInput) > servicePrice;

    const handleApplyAdjustment = async () => {
        if (!booking?.id || !organization?.id || adjustmentExceedsPrice) return;
        const value = Math.round(Number(adjustmentInput)) || 0;
        const signed = adjustmentType === 'discount' ? -value : value;
        if (signed === localAdjustment) return;

        setIsDiscounting(true);
        try {
            const result = await updateBooking(booking.id, { price_adjustment: signed });
            if (!result.success) {
                toast.error(result.error || t('common.error'));
                return;
            }
            setLocalAdjustment(signed);
            if (signed < 0) {
                const rec = await recordBookingDiscount({
                    booking_id: Number(booking.id),
                    shop_id: Number(organization.id),
                    type: 'fixed',
                    value,
                    amount: value,
                    applied_by: user?.id ?? null,
                });
                if (rec.success && rec.data) setDiscount(rec.data);
            } else {
                setDiscount(null);
            }
            toast.success(t('bookings.modal.adjustment_saved', 'Prezzo aggiornato'));
            // Modifica fatta: il dettaglio si chiude da solo
            onClose();
        } catch (error) {
            console.error('Error applying adjustment:', error);
            toast.error(t('common.error'));
        } finally {
            setIsDiscounting(false);
        }
    };

    // Cambio servizio dal dettaglio: è la modifica più frequente e nel mockup si fa qui.
    const serviceOptions = useMemo(() => {
        const list = booking?.hairdresser_id ? getPerformancesByHairdresser(booking.hairdresser_id) : [];
        return (list || [])
            .filter((p: any) => p.is_active !== false)
            .map((p: any) => ({
                value: p.id,
                label: `${p.name || 'Servizio'} · ${p.duration ?? 0} min${p.price ? ` · €${p.price}` : ''}`,
            }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [booking?.hairdresser_id, booking?.id]);

    const [isChangingService, setIsChangingService] = useState(false);
    const handleChangeService = async (newPerformanceId: string) => {
        if (!booking?.id || newPerformanceId === booking.performance_id) return;
        setIsChangingService(true);
        try {
            const result = await updateBooking(booking.id, { performance_id: newPerformanceId });
            if (result.success) {
                toast.success(t('bookings.modal.service_changed', 'Servizio aggiornato'));
                // Modifica fatta: il dettaglio si chiude da solo
                onClose();
            } else {
                toast.error(result.error || t('common.error'));
            }
        } finally {
            setIsChangingService(false);
        }
    };

    const handleToggleBlock = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!client?.id) return;
        setConfirmAction(isBlocked ? 'unblock' : 'block');
    };

    const executeBlockToggle = async () => {
        if (!client?.id) return;
        setIsActionLoading(true);
        try {
            if (isBlocked) {
                const res = await unlockClient(client.id);
                if (res.success) toast.success(t('customers.modal.toast.unblock_success', 'Cliente sbloccato con successo'));
                else toast.error(res.error || t('common.error'));
            } else {
                const res = await blockClient(client.id);
                if (res.success) toast.success(t('customers.modal.toast.block_success', 'Cliente bloccato con successo'));
                else toast.error(res.error || t('common.error'));
            }
        } finally {
            setIsActionLoading(false);
            setConfirmAction(null);
        }
    };

    const handleReportConfirm = async (reason: string) => {
        if (!client?.id || !booking?.id) return;
        setIsReporting(true);

        try {
            const result = await createCustomerReport({
                client_id: client.id,
                reason,
                booking_id: booking.id
            });

            if (result.success) {
                toast.success(t('bookings.modal.report_success', 'Cliente segnalato con successo'));

                if (willBlockOnNextReport) {
                    await fetchBlackList();
                }
            } else {
                toast.error(result.error || t('common.error'));
            }
        } catch (error) {
            console.error(error);
            toast.error(t('common.error'));
        } finally {
            setIsReporting(false);
            setIsReportModalOpen(false);
        }
    };

    if (!isOpen || !booking) return null;

    // --- LOGICA DATI ---

    const formattedDate = booking.date
        ? format(new Date(booking.date), 'EEE, d MMMM', { locale: it })
        : '';

    const startTime = booking.time ? booking.time.slice(0, 5) : '';
    let endTime = '';
    if (startTime) {
        const [h, m] = startTime.split(':').map(Number);
        const durationMinutes = performance?.duration || 30;
        const endDate = new Date();
        endDate.setHours(h, m + durationMinutes);
        endTime = format(endDate, 'HH:mm');
    }

    const staffColor = 'bg-gray-200 text-gray-700';
    const staffInitials = hairdresser
        ? `${hairdresser.first_name?.[0] || ''}${hairdresser.last_name?.[0] || ''}`.toUpperCase()
        : '?';


    // --- GESTIONE CANCELLAZIONE ---
    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = async () => {
        if (!booking?.id) return;

        setIsDeleting(true);
        try {
            let result;
            const isSoftDelete = !client?.is_manually_entry;

            // 1. Esegui l'operazione sul Database
            if (isSoftDelete) {
                result = await updateBooking(booking.id, { status: 'cancelled' });
            } else {
                result = await deleteBooking(booking.id);
            }

            if (!result.success) {
                toast.error(result.error || t('bookings.modal.delete_error'));
                setIsDeleting(false);
                return;
            }

            // 2. Feedback finale e chiusura
            toast.success(t('bookings.modal.delete_success'));
            setShowDeleteConfirm(false);
            onClose();

        } catch (error) {
            console.error('Errore inaspettato:', error);
            toast.error(t('bookings.modal.delete_unexpected'));
            setIsDeleting(false);
        }
    };

    return (
        <>
            {/* MODALE DETTAGLI (UI Moderna) */}
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                className="max-w-lg"
                compact
                headerClassName="bg-white"
                closeButtonClassName="bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700"
                headerContent={
                    <div className="pr-10">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-clip-600 mb-1">
                            {t('bookings.modal.eyebrow', 'Dettagli prenotazione')}
                        </p>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                            {performance?.name || t('bookings.modal.service_not_found')}
                        </h2>
                        {/* Quando, con chi, quanto dura: era l'informazione che mancava
                            e costringeva a chiudere il dettaglio per guardare l'agenda. */}
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                            {bookingStart && (
                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-clip-700 bg-clip-50 border border-clip-100 rounded-full px-2.5 py-1 capitalize">
                                    <Calendar size={12} />
                                    {format(bookingStart, 'EEEE d MMMM', { locale: i18n.language === 'it' ? it : enUS })}
                                </span>
                            )}
                            {booking.time && (
                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-clip-700 bg-clip-50 border border-clip-100 rounded-full px-2.5 py-1">
                                    <Clock size={12} />
                                    {booking.time.slice(0, 5)}
                                </span>
                            )}
                            {performance?.duration && (
                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-clip-700 bg-clip-50 border border-clip-100 rounded-full px-2.5 py-1">
                                    {performance.duration} min
                                </span>
                            )}
                            {hairdresser && (
                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-clip-700 bg-clip-50 border border-clip-100 rounded-full px-2.5 py-1">
                                    <Scissors size={12} />
                                    {`${hairdresser.first_name ?? ''} ${hairdresser.last_name ?? ''}`.trim() || hairdresser.name}
                                </span>
                            )}
                        </div>
                        {booking.created_at && (
                            <div className="text-xs text-gray-400 flex items-center gap-1.5 mt-2">
                                <Clock size={12} />
                                <span>
                                    {t('bookings.modal.created_at', 'Prenotazione effettuata il: {{date}}', {
                                        date: format(new Date(booking.created_at), 'd MMMM yyyy - HH:mm', { locale: i18n.language === 'it' ? it : enUS })
                                    })}
                                </span>
                            </div>
                        )}
                    </div>
                }
            >
                <div className="space-y-2.5">

                    {(isBlocked || customerReports.length > 0 || localStatus === 'no_show') && (
                        <div className="flex flex-col gap-2">
                            {/* Banner Segnalazioni e Blocco */}
                            {isBlocked ? (
                                <div className="bg-red-50 border border-red-200 rounded-2xl p-3 flex gap-3">
                                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                                        <AlertTriangle size={20} className="text-red-500" />
                                    </div>
                                    <div>
                                        <h4 className="text-red-900 font-bold text-sm">
                                            {t('bookings.modal.client_blocked_title', 'Attenzione: Cliente bloccato')}
                                        </h4>
                                        <p className="text-red-700 text-xs mt-0.5">
                                            {t('bookings.modal.client_blocked_desc', 'Questo cliente è bloccato e non può effettuare prenotazioni')}
                                        </p>
                                    </div>
                                </div>
                            ) : customerReports.length > 0 ? (
                                <div className="bg-orange-50 border border-orange-200 rounded-2xl p-3 flex gap-3">
                                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                                        <AlertTriangle size={20} className="text-orange-500" />
                                    </div>
                                    <div>
                                        <h4 className="text-orange-900 font-bold text-sm">
                                            {t('bookings.modal.report_warning', 'Attenzione: Cliente segnalato')} ({customerReports.length}/{maxReportsLimit})
                                        </h4>
                                        <p className="text-orange-700 text-xs mt-0.5">
                                            {t('bookings.modal.report_reason', 'Motivo della segnalazione:')} <span className="font-semibold italic">"{customerReports.find((r: any) => r.reason)?.reason || t('bookings.modal.report_no_reason', 'Nessun motivo specificato')}"</span>
                                        </p>
                                    </div>
                                </div>
                            ) : null}

                            {/* Banner No-Show */}
                            {localStatus === 'no_show' && (
                                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-3 flex gap-3 items-center">
                                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                        <UserX size={20} className="text-blue-500" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="text-blue-900 font-bold text-sm">
                                            {t('bookings.modal.no_show_banner_title', 'Mancata presentazione (No-Show)')}
                                        </h4>
                                        <p className="text-blue-700 text-xs mt-0.5">
                                            {t('bookings.modal.no_show_banner_desc', 'Il cliente non si è presentato a questo appuntamento.')}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleUndoNoShow}
                                        disabled={isUpdatingStatus}
                                        className="text-xs font-bold text-blue-700 hover:text-white hover:bg-blue-600 transition-colors bg-white px-3 py-1.5 rounded-lg border border-blue-300 flex-shrink-0 disabled:opacity-50 whitespace-nowrap"
                                    >
                                        {t('bookings.modal.no_show_undo', 'Ripristina confermato')}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Cliente: chi è, come si chiama, quanto paga */}
                    <div
                        className="bg-gray-50 border border-gray-200 rounded-2xl p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => client && onOpenCustomer?.(client)}
                    >
                        <div className="w-11 h-11 flex-shrink-0 rounded-full bg-clip-50 border border-clip-100 flex items-center justify-center text-sm font-bold uppercase text-clip-600">
                            {client?.full_name
                                ? client.full_name.split(' ').filter(Boolean).slice(0, 2).map((w: string) => w[0]).join('')
                                : '?'}
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="font-bold text-gray-900 truncate">
                                {client ? client.full_name : t('bookings.modal.client_not_found')}
                            </p>
                            <p className="text-sm text-gray-500 truncate">
                                {client?.phone || client?.email || t('bookings.modal.no_contact', 'Nessun contatto')}
                            </p>
                        </div>
                        {servicePrice > 0 && (
                            <div className="flex-shrink-0 text-right">
                                <span className="text-base font-bold text-gray-900 tabular-nums">
                                    €{Math.max(0, servicePrice + localAdjustment)}
                                </span>
                                {localAdjustment !== 0 && (
                                    <span className="block text-xs text-gray-400 line-through tabular-nums">
                                        €{servicePrice}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Contatto diretto */}
                    {client?.phone && (
                        <div className={`grid gap-2 ${getWhatsAppUrl(client.phone) ? 'grid-cols-2' : 'grid-cols-1'}`}>
                            <a
                                href={`tel:${client.phone}`}
                                className="flex items-center justify-center gap-2 py-2 rounded-xl border border-gray-200 text-gray-700 font-bold hover:bg-gray-50 transition-colors"
                            >
                                <PhoneCall size={16} />
                                {t('bookings.modal.call_client', 'Chiama')}
                            </a>
                            {getWhatsAppUrl(client.phone) && (
                                <a
                                    href={getWhatsAppUrl(client.phone)!}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-2 py-2 rounded-xl bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold transition-colors"
                                >
                                    <MessageCircle size={16} />
                                    WhatsApp
                                </a>
                            )}
                        </div>
                    )}

                    {/* Note tecniche del cliente: si vedono aprendo l'appuntamento,
                        non dietro un altro click */}
                    <TechnicalNotesPanel clientId={client?.id} variant="compact" />

                    {/* Servizio: la modifica più frequente, si fa senza cambiare schermata */}
                    <div className="space-y-2 relative z-[40]">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                            {t('new_appointment.service_label', 'Servizio')}
                        </label>
                        <CustomSelect
                            value={booking.performance_id}
                            onChange={handleChangeService}
                            options={serviceOptions}
                            placeholder={performance?.name || t('bookings.modal.service_not_found')}
                            disabled={isChangingService || localStatus !== 'confirmed'}
                        />
                    </div>

                    {/* No-show: un solo click lo registra subito. La segnalazione è
                        un'azione a parte, sempre a portata di mano. */}
                    <div className="grid grid-cols-2 gap-2">
                        {localStatus === 'confirmed' ? (
                            <button
                                type="button"
                                onClick={handleNoShow}
                                disabled={isUpdatingStatus}
                                className="flex items-center justify-center gap-2 py-2 rounded-xl border border-red-200 text-red-600 font-bold hover:bg-red-50 transition-colors disabled:opacity-50"
                            >
                                {isUpdatingStatus ? <Loader2 size={16} className="animate-spin" /> : <UserX size={16} />}
                                {t('bookings.modal.mark_no_show', 'No-show')}
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={handleUndoNoShow}
                                disabled={isUpdatingStatus}
                                className="flex items-center justify-center gap-2 py-2 rounded-xl border border-blue-200 text-blue-700 font-bold hover:bg-blue-50 transition-colors disabled:opacity-50"
                            >
                                {t('bookings.modal.no_show_undo', 'Ripristina confermato')}
                            </button>
                        )}

                        <button
                            type="button"
                            onClick={() => setIsReportModalOpen(true)}
                            disabled={!client?.id || isBlocked || customerReports.length >= maxReportsLimit}
                            title={
                                isBlocked
                                    ? t('bookings.modal.report_disabled_blocked', 'Il cliente è già bloccato')
                                    : customerReports.length >= maxReportsLimit
                                        ? t('bookings.modal.report_disabled_limit', 'Limite segnalazioni raggiunto')
                                        : undefined
                            }
                            className="flex items-center justify-center gap-2 py-2 rounded-xl border border-orange-200 text-orange-600 font-bold hover:bg-orange-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <AlertTriangle size={16} />
                            {t('bookings.modal.report_btn', 'Segnala')}
                        </button>
                    </div>

                    {isBlocked && (
                        <button
                            type="button"
                            onClick={handleToggleBlock}
                            className="w-full flex items-center justify-center gap-2 py-2 rounded-xl border border-green-200 text-green-700 font-bold hover:bg-green-50 transition-colors"
                        >
                            <LockOpen size={16} />
                            {t('customers.modal.unblock', 'Sblocca')}
                        </button>
                    )}

                    {/* Sconto o maggiorazione a valore libero */}
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                            {t('new_appointment.discount_or_markup_label', 'Sconto o Maggiorazione')}
                        </label>
                        <div className="flex items-center gap-3">
                            <div className="flex bg-gray-100 rounded-xl p-1 flex-shrink-0">
                                {([
                                    { value: 'discount', label: t('new_appointment.adjustment_type_discount', 'Sconto'), active: 'text-emerald-600' },
                                    { value: 'markup', label: t('bookings.modal.adjustment_add', 'Aggiungi'), active: 'text-blue-600' },
                                ] as const).map((opt) => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => setAdjustmentType(opt.value)}
                                        disabled={localStatus !== 'confirmed'}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors disabled:opacity-50 ${adjustmentType === opt.value ? `bg-white shadow-sm ${opt.active}` : 'text-gray-500 hover:text-gray-700'}`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                            <div className="relative flex-1">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">
                                    {adjustmentType === 'discount' ? '-€' : '+€'}
                                </span>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    placeholder="0"
                                    value={adjustmentInput}
                                    onChange={(e) => setAdjustmentInput(e.target.value)}
                                    onBlur={handleApplyAdjustment}
                                    disabled={isDiscounting || localStatus !== 'confirmed'}
                                    className={`w-full pl-10 pr-3 py-2.5 bg-white border rounded-xl outline-none font-medium tabular-nums focus:ring-2 focus:ring-clip-200 disabled:bg-gray-50 disabled:text-gray-400 ${adjustmentExceedsPrice ? 'border-red-400 text-red-900' : 'border-gray-200 text-gray-900'}`}
                                />
                            </div>
                        </div>
                        <p className={`text-xs ${adjustmentExceedsPrice ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
                            {adjustmentExceedsPrice
                                ? t('new_appointment.discount_too_high_error', 'Lo sconto supera il prezzo del servizio (€{{price}})', { price: Math.round(servicePrice) })
                                : adjustmentType === 'discount'
                                    ? t('bookings.modal.discount_hint', 'Sconto in euro sul prezzo.')
                                    : t('bookings.modal.markup_hint', 'Maggiorazione in euro sul prezzo.')}
                        </p>
                        {discount && discountAppliedBy && (
                            <p className="text-xs text-gray-400">
                                {t('bookings.modal.discount_applied_by', 'applicato da {{name}}', { name: discountAppliedBy })}
                                {discount.created_at && ` · ${format(new Date(discount.created_at), 'd MMM yyyy', { locale: i18n.language === 'it' ? it : enUS })}`}
                            </p>
                        )}
                    </div>

                    {/* Promemoria inviati: informazione di servizio, sta in coda */}
                    {(booking.reminder_24h_sent_at || booking.reminder_1h_sent_at) && (
                        <div className="text-xs text-gray-400 space-y-1">
                            {booking.reminder_24h_sent_at && (
                                <div className="flex items-center gap-1.5">
                                    <Bell size={12} />
                                    <span>
                                        {t('bookings.modal.reminder_24h_sent_at', 'Promemoria 24h inviato il: {{date}}', {
                                            date: format(new Date(booking.reminder_24h_sent_at), 'd MMMM yyyy - HH:mm', { locale: i18n.language === 'it' ? it : enUS })
                                        })}
                                    </span>
                                </div>
                            )}
                            {booking.reminder_1h_sent_at && (
                                <div className="flex items-center gap-1.5">
                                    <Bell size={12} />
                                    <span>
                                        {t('bookings.modal.reminder_1h_sent_at', 'Promemoria 1h inviato il: {{date}}', {
                                            date: format(new Date(booking.reminder_1h_sent_at), 'd MMMM yyyy - HH:mm', { locale: i18n.language === 'it' ? it : enUS })
                                        })}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Footer: solo elimina. Chiudere si fa con la X. */}
                    <div className="pt-3 border-t border-gray-100 grid gap-2 grid-cols-1">
                        <button
                            type="button"
                            onClick={handleDeleteClick}
                            disabled={isDeleting}
                            className="flex items-center justify-center gap-2 py-2 rounded-xl border border-red-100 bg-red-50 text-red-600 font-bold hover:bg-red-100 transition-colors disabled:opacity-50"
                        >
                            {isDeleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                            {t('common.delete', 'Elimina')}
                        </button>
                    </div>

                </div>
            </Modal>

            {/* MODALE DI CONFERMA CANCELLAZIONE */}
            <ConfirmDialog
                isOpen={showDeleteConfirm}
                onCancel={() => setShowDeleteConfirm(false)}
                onConfirm={handleConfirmDelete}
                title={t('bookings.modal.delete_confirm_title', 'Elimina Appuntamento')}
                description={t('bookings.modal.delete_confirm_message', 'Sei sicuro di voler eliminare questo appuntamento? Questa azione non può essere annullata.')}
                confirmLabel={t('common.delete', 'Elimina')}
                cancelLabel={t('common.cancel', 'Annulla')}
                variant="danger"
                loading={isDeleting}
            />

            {/* MODALE DI CONFERMA BLOCCO/SBLOCCO */}
            <ConfirmDialog
                isOpen={!!confirmAction}
                onCancel={() => !isActionLoading && setConfirmAction(null)}
                onConfirm={executeBlockToggle}
                title={confirmAction === 'block' ? t('customers.modal.block_title', "Blocca Cliente") : t('customers.modal.unblock_title', "Sblocca Cliente")}
                description={confirmAction === 'block' ? t('customers.modal.block_confirm_desc', { name: `${client?.full_name}` }) : t('customers.modal.unblock_confirm_desc', { name: `${client?.full_name}` })}
                confirmLabel={confirmAction === 'block' ? t('customers.modal.block', "Blocca") : t('customers.modal.unblock', "Sblocca")}
                cancelLabel={t('common.cancel', 'Annulla')}
                variant={confirmAction === 'block' ? 'danger' : 'default'}
                loading={isActionLoading}
            />

            {/* MODALE SEGNALAZIONE */}
            <ReportCustomerModal
                isOpen={isReportModalOpen}
                onClose={() => setIsReportModalOpen(false)}
                onConfirm={handleReportConfirm}
                clientName={client?.full_name}
                isLoading={isReporting}
            />
        </>
    );
};

export default BookingModal;